import AdditionalMeasurementDialog from '../../../components/Quality/AdditionalMeasurementDialog';
import { eventDeleteError } from '../../../utils/eventDeleteError';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Button, Chip, Dropdown, Menu, MenuButton, MenuItem, IconButton, Snackbar, Tooltip, Typography } from "@mui/joy";
import { DragDropContext, Draggable } from "@hello-pangea/dnd";

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';

import { LayoutContext } from "../../../context";
import {
    APIGetKanbanEvents, APIGetKanbanApplications, APIKanbanMoveEvent, APIDeleteEvent, APIGetKanbanSummary,
    APISetEventRevision, APIGetKanbanFilterOptions,
    APIGetKanbanFilters, APICreateKanbanFilter, APIDeleteKanbanFilter,
} from "../../../api";
import ApplicationCard from "../../../components/ApplicationsPanel/ApplicationCard";
import ModalImportApplications from "../../../components/Modals/ModalImportApplications";
import KanbanColumn from "./KanbanColumn";
import KanbanEventCard from "./KanbanEventCard";
import KanbanFilterBar from "./KanbanFilterBar";
import ModalKanbanTransition from "./ModalKanbanTransition";
// Смена стадии из меню карточки идёт тем же путём, что drag & drop:
// один эндпоинт (kanban_move_event) и один словарь колонок.
import ModalStagePicker from "../../../components/Modals/ModalStagePicker";
import AdditionalStopDialog from '../../../components/Quality/AdditionalStopDialog';
import { additionalStopPayload } from '../../../components/Quality/additionalActionsUi';
import ModalDraftsmanManage from "../../../components/Modals/ModalDraftsmanManage";
import { useOpenEventChat } from "../../../hooks/useOpenEventChat";

const COLUMNS = [
    { key: 'applications',       title: 'Заявки',            accent: '#7b5cff',        softBg: 'rgba(123,92,255,0.08)', type: 'applications' },
    { key: 'pending_assignment', title: 'На согласовании',   accent: '#6a7bff',        softBg: 'rgba(106,123,255,0.10)', type: 'event' },
    { key: 'measuring_assigned', title: 'Назначен обмер',     accent: 'cornflowerblue', softBg: 'rgba(100,149,237,0.10)', type: 'event' },
    { key: 'closed_by_surveyor', title: 'Закрыт замерщиком',  accent: '#12b76a',        softBg: 'rgba(18,183,106,0.10)',  type: 'event' },
    { key: 'in_drawing',         title: 'Взят в черчение',    accent: '#f79009',        softBg: 'rgba(247,144,9,0.10)',   type: 'event' },
    // Контроль качества — между черчением и завершением: чертёж сдан, но
    // клиенту ещё не ушёл. Порядок колонок тут и на сервере совпадает.
    { key: 'quality_check',      title: 'Контроль качества',  accent: '#7b5cff',        softBg: 'rgba(123,92,255,0.10)',  type: 'event' },
    { key: 'ready_for_delivery', title: 'Готово к выдаче', accent: '#059669', softBg: 'rgba(5,150,105,0.10)', type: 'event' },
    { key: 'completed',          title: 'Завершён',           accent: '#039855',        softBg: 'rgba(3,152,85,0.10)',    type: 'event' },
    { key: 'cancelled',          title: 'Отменён',            accent: '#cd1f1f',        softBg: 'rgba(205,31,31,0.10)',   type: 'event' },
];
const EMPTY_STATE = { items: [], page: 1, hasNext: true, loading: false, count: 0, order: 'desc' };
const EVENT_COLS = COLUMNS.filter(c => c.type === 'event').map(c => c.key);

// Секунды → короткая длительность
function fmtSecShort(sec) {
    if (sec == null || !Number.isFinite(sec) || sec < 0) return '—';
    const m = Math.floor(sec / 60);
    const d = Math.floor(m / 1440);
    const h = Math.floor((m % 1440) / 60);
    if (d) return `${d}д ${h}ч`;
    const mm = m % 60;
    return h ? `${h}ч ${mm}м` : `${mm}м`;
}

// Стрелка тренда относительно прошлого месяца.
// lowerIsBetter=true → меньше значение = лучше (для длительностей и % просрочек).
function TrendVsPrev({ cur, prev, lowerIsBetter = true }) {
    if (cur == null || prev == null || !Number.isFinite(cur) || !Number.isFinite(prev) || prev === 0) return null;
    const diffPct = Math.round((cur - prev) / prev * 100);
    if (diffPct === 0) {
        return <Typography component="span" level="body-xs" sx={{ ml: 0.5, color: 'text.tertiary' }}>≈ пр.мес</Typography>;
    }
    const better = lowerIsBetter ? diffPct < 0 : diffPct > 0;
    return (
        <Typography component="span" level="body-xs"
            sx={{ ml: 0.5, fontWeight: 700, color: better ? 'success.600' : 'danger.500' }}>
            {diffPct < 0 ? '↓' : '↑'}{Math.abs(diffPct)}% к пр.мес
        </Typography>
    );
}

export default function KanbanBoard({ onSummary }) {
    const {
        tokenEmployee, boardRefreshKey, bumpBoardRefresh,
        setModalEvent, setModalApplication, setChatModal,
        employeeAll,
    } = useContext(LayoutContext);
    const { openEventChat } = useOpenEventChat();

    const [cols, setCols] = useState(() =>
        Object.fromEntries(COLUMNS.map(c => [c.key, { ...EMPTY_STATE }]))
    );
    const colsRef = useRef(cols);
    useEffect(() => { colsRef.current = cols; }, [cols]);

    const [expandedApp, setExpandedApp] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const [toast, setToast] = useState('');
    const [pendingMove, setPendingMove] = useState(null); // { event, from, to, requiredFields }
    const [additionalMove, setAdditionalMove] = useState(null);
    const [additionalBusy, setAdditionalBusy] = useState(false);
    const [additionalError, setAdditionalError] = useState('');
    const additionalFlight = useRef(false);
    const [stageModal, setStageModal] = useState({ open: false, event: null });
    const [draftsmanModal, setDraftsmanModal] = useState({ open: false, event: null });
    const [summary, setSummary] = useState(null);

    // ── фильтр доски (Этап 2) ──
    const [filters, setFilters] = useState({});
    const filtersRef = useRef(filters);
    useEffect(() => { filtersRef.current = filters; }, [filters]);
    const [filterOptions, setFilterOptions] = useState(null);
    const [savedFilters, setSavedFilters] = useState([]);

    const token = tokenEmployee;
    const activeTokenRef = useRef(token);
    activeTokenRef.current = token;
    const requestsRef = useRef({});
    const summaryRequestRef = useRef(0);
    const filterRevisionRef = useRef(0);


    // ── загрузка одной колонки ──
    const loadColumn = useCallback(async (key, { reset = false, order } = {}) => {
        if (!token) return;
        const cur = colsRef.current[key];
        if (!cur || (!reset && (requestsRef.current[key]?.running || !cur.hasNext))) return;
        const request = { running: true };
        requestsRef.current[key] = request;
        const isCurrent = () => requestsRef.current[key] === request && activeTokenRef.current === token;
        const filterSnapshot = { ...filtersRef.current };
        const ord = order || cur.order || 'desc';
        const page = reset ? 1 : cur.page;
        setCols(prev => ({ ...prev, [key]: { ...prev[key], ...(reset ? { items: [], count: 0, hasNext: true, page: 1 } : {}), loading: true, order: ord } }));
        try {
            const data = key === 'applications'
                ? await APIGetKanbanApplications({ page, page_size: 10, order: ord, filters: filterSnapshot }, token)
                : await APIGetKanbanEvents({ stage: key, page, page_size: 10, order: ord, filters: filterSnapshot }, token);
            if (!isCurrent()) return;
            setCols(prev => {
                if (!isCurrent()) return prev;
                const base = reset ? [] : prev[key].items;
                const seen = new Set(base.map(x => x.id));
                const merged = [...base, ...(data?.results || []).filter(x => !seen.has(x.id))];
                return { ...prev, [key]: {
                    items: merged, page: (data?.page || page) + 1,
                    hasNext: !!data?.has_next, loading: false,
                    count: data?.count ?? merged.length, order: ord,
                } };
            });
        } catch (_) {
            if (isCurrent()) setToast('Не удалось обновить колонку. Повторите загрузку.');
        } finally {
            if (isCurrent()) {
                request.running = false;
                setCols(prev => ({ ...prev, [key]: { ...prev[key], loading: false } }));
            }
        }
    }, [token]);

    // ── переключение сортировки колонки ──
    const toggleColumnOrder = useCallback((key) => {
        const next = (colsRef.current[key]?.order || 'desc') === 'desc' ? 'asc' : 'desc';
        setCols(prev => ({
            ...prev,
            [key]: { ...prev[key], order: next, items: [], page: 1, hasNext: true },
        }));
        loadColumn(key, { reset: true, order: next });
    }, [loadColumn]);

    // ── кнопка-переключатель в шапке колонки ──
    const SortToggle = ({ colKey }) => {
        const ord = cols[colKey]?.order || 'desc';
        return (
            <Tooltip
                size="sm"
                title={ord === 'desc' ? 'Сейчас: сначала поздние даты' : 'Сейчас: сначала ранние даты'}
            >
                <IconButton
                    size="sm" variant="plain" color="neutral"
                    onClick={() => toggleColumnOrder(colKey)}
                >
                    {ord === 'desc'
                        ? <ArrowDownwardRoundedIcon sx={{ fontSize: 18 }} />
                        : <ArrowUpwardRoundedIcon sx={{ fontSize: 18 }} />}
                </IconButton>
            </Tooltip>
        );
    };

    // ── сводка (счётчики, месячные показатели) ──
    const loadSummary = useCallback(async () => {
        if (!token) return;
        const request = ++summaryRequestRef.current;
        const data = await APIGetKanbanSummary(token, { ...filtersRef.current });
        if (request !== summaryRequestRef.current || activeTokenRef.current !== token) return;
        setSummary(data);
        onSummary?.(data);
    }, [token, onSummary]);

    // ── первичная загрузка + реакция на boardRefreshKey ──
    useEffect(() => {
        COLUMNS.forEach(c => loadColumn(c.key, { reset: true }));
        loadSummary();
        return () => {
            requestsRef.current = {};
            summaryRequestRef.current += 1;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boardRefreshKey, token]);

    // ── справочники фильтра ──
    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setFilterOptions(null);
        APIGetKanbanFilterOptions(token).then(data => { if (!cancelled) setFilterOptions(data); });
        return () => { cancelled = true; };
    }, [token]);

    // ── применить / сбросить фильтр ──
    const applyFilters = useCallback((next) => {
        const val = next || {};
        filterRevisionRef.current += 1;
        setFilters(val);
        filtersRef.current = val;   // синхронно, чтобы перезагрузка увидела актуальные параметры
        COLUMNS.forEach((c) => loadColumn(c.key, { reset: true }));
        loadSummary();
    }, [loadColumn, loadSummary]);
    const resetFilters = useCallback(() => applyFilters({}), [applyFilters]);

    // ── сохранённые фильтры (пресеты) ──
    const loadSavedFilters = useCallback(async () => {
        const list = await APIGetKanbanFilters(token);
        const arr = Array.isArray(list) ? list : [];
        if (activeTokenRef.current === token) setSavedFilters(arr);
        return arr;
    }, [token]);

    useEffect(() => {
        if (!token) return;
        const revision = filterRevisionRef.current;
        let cancelled = false;
        setSavedFilters([]);
        loadSavedFilters().then((list) => {
            if (cancelled || activeTokenRef.current !== token || filterRevisionRef.current !== revision) return;
            // авто-применяем пресет «по умолчанию» владельца (один раз, при заходе)
            const def = list.find((f) => f.is_default && f.is_mine);
            if (def) applyFilters(def.params || {});
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const savePreset = useCallback(async (preset) => {
        const created = await APICreateKanbanFilter(preset, token);
        if (created) await loadSavedFilters();
    }, [token, loadSavedFilters]);

    const deletePreset = useCallback(async (preset) => {
        if (!window.confirm(`Удалить фильтр «${preset.name}»?`)) return;
        await APIDeleteKanbanFilter(preset.id, token);
        await loadSavedFilters();
    }, [token, loadSavedFilters]);

    const applyPreset = useCallback((preset) => applyFilters(preset?.params || {}), [applyFilters]);

    // ── мутаторы локального состояния при drag ──
    const moveCardLocal = (from, to, id, index) => {
        setCols(prev => {
            const card = prev[from].items.find(e => e.id === id);
            if (!card) return prev;
            const fromItems = prev[from].items.filter(e => e.id !== id);
            const toItems = [...prev[to].items];
            toItems.splice(Math.min(index, toItems.length), 0, card);
            return {
                ...prev,
                [from]: { ...prev[from], items: fromItems, count: Math.max(0, prev[from].count - 1) },
                [to]:   { ...prev[to],   items: toItems,   count: prev[to].count + 1 },
            };
        });
    };
    const replaceCard = (col, id, fresh) => {
        if (!fresh) return;
        setCols(prev => ({
            ...prev,
            [col]: { ...prev[col], items: prev[col].items.map(e => e.id === id ? fresh : e) },
        }));
    };
    const revertMove = (from, to, id, card) => {
        setCols(prev => {
            const toItems = prev[to].items.filter(e => e.id !== id);
            const exists = prev[from].items.some(e => e.id === id);
            const fromItems = exists ? prev[from].items : [card, ...prev[from].items];
            return {
                ...prev,
                [to]:   { ...prev[to],   items: toItems,   count: Math.max(0, prev[to].count - 1) },
                [from]: { ...prev[from], items: fromItems, count: prev[from].count + (exists ? 0 : 1) },
            };
        });
    };

    // ── drag end ──
    const onDragEnd = (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        const from = source.droppableId;
        const to = destination.droppableId;
        if (to === 'applications' || from === 'applications') return;
        if (from === to && source.index === destination.index) return;

        const id = Number(String(draggableId).replace('evt-', ''));
        const card = colsRef.current[from]?.items.find(e => e.id === id);
        if (!card) return;
        if (to === 'completed' && !window.confirm('Выдать проверенные материалы клиенту и зафиксировать завершение?')) return;

        // оптимистично двигаем карточку (появляется в новой колонке)
        moveCardLocal(from, to, id, destination.index);

        // проба без payload
        APIKanbanMoveEvent(id, { target_stage: to, expected_qa_round: card.qa_round }, token).then(res => {
            if (res?.status === 'ok') {
                replaceCard(to, id, res.event);
                loadSummary();
            } else if (res?.code === 'additional_confirmation_required') {
                revertMove(from, to, id, card);
                setAdditionalError('');
                setAdditionalMove({ event: card, from, to, index: destination.index, cycle: res.additional_measurement });
            } else if (res?.code === 'data_required') {
                setPendingMove({ event: card, from, to, requiredFields: res.required_fields || [] });
            } else {
                revertMove(from, to, id, card);
                setToast(res?.message || 'Не удалось переместить');
            }
        });
    };

    const confirmAdditionalMove = async reason => {
        if (additionalFlight.current || !additionalMove) return;
        additionalFlight.current = true; setAdditionalBusy(true); setAdditionalError('');
        const intent = additionalMove;
        const extra = additionalStopPayload(intent.cycle, reason);
        try {
            const res = await APIKanbanMoveEvent(intent.event.id, { target_stage: intent.to,
                expected_qa_round: intent.event.qa_round, ...extra }, token);
            if (res?.status === 'ok') {
                moveCardLocal(intent.from, intent.to, intent.event.id, intent.index);
                replaceCard(intent.to, intent.event.id, res.event); loadSummary(); setAdditionalMove(null);
            } else if (res?.code === 'data_required') {
                moveCardLocal(intent.from, intent.to, intent.event.id, intent.index);
                setPendingMove({ ...intent, extra, requiredFields: res.required_fields || [] });
                setAdditionalMove(null);
            } else setAdditionalError(res?.message || res?.detail || 'Не удалось сменить стадию');
        } catch (_) { setAdditionalError('Ошибка связи. Обновите доску перед повтором.'); }
        finally { additionalFlight.current = false; setAdditionalBusy(false); }
    };

    // ── подтверждение мини-модалки ──
    const handleTransitionConfirm = async (payload) => {
        const { event, to } = pendingMove;
        const res = await APIKanbanMoveEvent(event.id, { target_stage: to, expected_qa_round: event.qa_round, ...pendingMove.extra, ...payload }, token);
        if (res?.status === 'ok') {
            replaceCard(to, event.id, res.event);
            setPendingMove(null);
            loadSummary();
            return { ok: true };
        }
        return { ok: false, message: res?.message || 'Ошибка' };
    };
    const handleTransitionCancel = () => {
        const { event, from, to } = pendingMove;
        revertMove(from, to, event.id, event);
        setPendingMove(null);
    };

    // ── убрать событие из его колонки ──
    const removeEventLocal = (id) => {
        setCols(prev => {
            const next = { ...prev };
            EVENT_COLS.forEach(k => {
                if (next[k].items.some(e => e.id === id)) {
                    next[k] = { ...next[k], items: next[k].items.filter(e => e.id !== id), count: Math.max(0, next[k].count - 1) };
                }
            });
            return next;
        });
    };

    // ── переложить событие в колонку по его kanban_stage (после смены стадии) ──
    const relocateEvent = (updated) => {
        if (!updated) return;
        const target = updated.kanban_stage;
        setCols(prev => {
            const next = { ...prev };
            EVENT_COLS.forEach(k => {
                if (next[k].items.some(e => e.id === updated.id)) {
                    next[k] = { ...next[k], items: next[k].items.filter(e => e.id !== updated.id), count: Math.max(0, next[k].count - 1) };
                }
            });
            if (target && next[target]) {
                next[target] = { ...next[target], items: [updated, ...next[target].items], count: next[target].count + 1 };
            }
            return next;
        });
    };

    // ── действия карточки события ──
    const openClientChat = (event) => event?.client_chat?.id &&
        setChatModal({ open: true, chatId: event.client_chat.id, chatName: event.client_chat.name || `Клиент · №${event?.bitrix_deal?.id || event.id}` });

    const changeStage = (event) => setStageModal({ open: true, event });
    const [additionalEvent, setAdditionalEvent] = useState(null);
    const manageDraftsman = (event) => setDraftsmanModal({ open: true, event });

    const toggleRevision = async (event) => {
        const turnOn = !event?.is_in_revision;
        const res = await APISetEventRevision(event.id, turnOn, '', token);
        if (res?.status === 'ok') {
            bumpBoardRefresh();
        } else {
            setToast(res?.message || 'Не удалось изменить статус доработки');
        }
    };

    const deleteEvent = async (event) => {
        if (!window.confirm(`Удалить событие №${event?.bitrix_deal?.id || event.id}?`)) return;
        try {
            const res = await APIDeleteEvent(token, event.id);
            if (res?.status === 'ok') {
                removeEventLocal(event.id);
                loadColumn('applications', { reset: true }); // вернувшаяся заявка появится в «Заявки»
                loadSummary();
            } else {
                setToast(eventDeleteError(res));
            }
        } catch (error) {
            setToast(eventDeleteError(error));
        }
    };

    // ── колбэки колонки «Заявки» ──
    const openEvent = (event) => setModalEvent({ event, open: true });
    
    const appCallbacks = (app) => ({
        onCreateEvent: () => setModalEvent({ event: null, open: true, preselectedApplication: app }),
        onEdit: () => setModalApplication({ open: true, application: app }),
        onOpenEvent: () => app?.event && setModalEvent({ event: app.event, open: true }),
        onOpenClientChat: () => app?.chat_info && setChatModal({ open: true, chatId: app.chat_info.id, chatName: app.chat_info.name || `Заявка #${app.id}` }),
        onOpenEventChat: () => app?.event && openEventChat(app.event),
        onDeleted: () => loadColumn('applications', { reset: true }),
    });

    return (
        <>
            <AdditionalMeasurementDialog open={!!additionalEvent} event={additionalEvent} onClose={() => setAdditionalEvent(null)} onChanged={() => { COLUMNS.forEach(c => loadColumn(c.key, { reset: true })); loadSummary(); }} />
            <KanbanFilterBar
                value={filters}
                options={filterOptions}
                saved={savedFilters}
                onApply={applyFilters}
                onReset={resetFilters}
                onSavePreset={savePreset}
                onDeletePreset={deletePreset}
                onApplyPreset={applyPreset}
            />
            <DragDropContext onDragEnd={onDragEnd}>
                <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1, alignItems: 'flex-start' }}>
                    {COLUMNS.map(col => {
                        const state = cols[col.key];

                        if (col.type === 'applications') {
                            return (
                                <KanbanColumn
                                    key={col.key}
                                    col={col}
                                    state={state}
                                    onLoadMore={() => loadColumn(col.key)}
                                    headerExtra={
                                        <Stack direction="row" alignItems="center" spacing={0.25}>
                                            <SortToggle colKey={col.key} />
                                            <Dropdown>
                                                <MenuButton slots={{ root: IconButton }}
                                                            slotProps={{ root: { size: 'sm', variant: 'plain', color: 'neutral' } }}>
                                                    <MoreVertRoundedIcon />
                                                </MenuButton>
                                                <Menu size="sm" sx={{ zIndex: 10102 }}>
                                                    <MenuItem onClick={() => setImportOpen(true)}>
                                                        <UploadFileRoundedIcon sx={{ fontSize: 18 }} /> Импорт из Excel
                                                    </MenuItem>
                                                </Menu>
                                            </Dropdown>
                                        </Stack>
                                    }
                                    subHeader={
                                        <Stack spacing={0.75}>
                                            <Button fullWidth size="sm" variant="soft" color="primary"
                                                    startDecorator={<AddRoundedIcon />}
                                                    onClick={() => setModalApplication({ open: true, application: null })}
                                                    sx={{ borderRadius: 'lg' }}>
                                                Создать заявку
                                            </Button>
                                            <Button fullWidth size="sm" variant="outlined" color="primary"
                                                    startDecorator={<AddRoundedIcon />}
                                                    onClick={() => setModalEvent({ event: null, open: true, preselectedApplication: null })}
                                                    sx={{ borderRadius: 'lg' }}>
                                                Создать событие
                                            </Button>
                                        </Stack>
                                    }
                                    renderCard={(app) => (
                                        <Box key={`app-${app.id}`} sx={{ mb: 1 }}>
                                            <ApplicationCard
                                                application={app}
                                                expanded={expandedApp === app.id}
                                                onToggleExpand={() => setExpandedApp(x => x === app.id ? null : app.id)}
                                                {...appCallbacks(app)}
                                            />
                                        </Box>
                                    )}
                                />
                            );
                        }

                        // Колонки событий (drop-зоны)
                        return (
                            <KanbanColumn
                                key={col.key}
                                col={col}
                                state={state}
                                droppableId={col.key}
                                onLoadMore={() => loadColumn(col.key)}
                                headerExtra={<SortToggle colKey={col.key} />}
                                subHeader={
                                    // ── Задача 6: «Завершён» — длительности + сравнение с прошлым месяцем ──
                                    col.key === 'completed' && summary?.completed_month ? (
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
                                            <Chip size="sm" variant="soft" color="success" sx={{ px: 2 }}>
                                                Обмер ~ {fmtSecShort(summary.completed_month.avg_surveyor_seconds)}
                                                <TrendVsPrev
                                                    cur={summary.completed_month.avg_surveyor_seconds}
                                                    prev={summary.completed_prev_month?.avg_surveyor_seconds}
                                                    lowerIsBetter />
                                            </Chip>
                                            <Chip size="sm" variant="soft" color="primary" sx={{ px: 2 }}>
                                                Черчение ~ {fmtSecShort(summary.completed_month.avg_draftsman_seconds)}
                                                <TrendVsPrev
                                                    cur={summary.completed_month.avg_draftsman_seconds}
                                                    prev={summary.completed_prev_month?.avg_draftsman_seconds}
                                                    lowerIsBetter />
                                            </Chip>
                                        </Stack>
                                    // ── Задачи 5 + 7: «Взят в черчение» — просрочки сейчас + тренд к прошлому месяцу ──
                                    ) : col.key === 'in_drawing' && summary?.in_drawing_deadlines ? (
                                        <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
                                            <Chip size="sm" variant="soft" color="success" startDecorator="✓">
                                                {summary.in_drawing_deadlines.ontime} в срок
                                            </Chip>
                                            <Chip size="sm" variant="soft" color="danger" startDecorator="⏰">
                                                {summary.in_drawing_deadlines.overdue} просроч.
                                            </Chip>
                                            <Chip size="sm" variant="soft"
                                                  color={summary.in_drawing_deadlines.overdue_percent > 0 ? 'danger' : 'success'}
                                                  sx={{ px: 2 }}
                                            >
                                                <Stack direction={'column'} alignItems={'center'}>
                                                    <Typography level="body-xs">
                                                        {summary.in_drawing_deadlines.overdue_percent}% просроч.
                                                    </Typography>
                                                    {summary?.drawing_late_rate && (
                                                        <TrendVsPrev
                                                            cur={summary.drawing_late_rate.this_month?.late_percent}
                                                            prev={summary.drawing_late_rate.prev_month?.late_percent}
                                                            lowerIsBetter />
                                                    )}
                                                </Stack>
                                            </Chip>
                                        </Stack>
                                    ) : undefined
                                }
                                renderCard={(event, i) => (
                                    <Draggable key={`evt-${event.id}`} draggableId={`evt-${event.id}`} index={i}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                style={{ ...provided.draggableProps.style, marginBottom: 8 }}
                                            >
                                                <KanbanEventCard
                                                    event={event}
                                                    stage={col.key}
                                                    dragHandleProps={provided.dragHandleProps}
                                                    isDragging={snapshot.isDragging}
                                                    onOpen={openEvent}
                                                    onOpenChat={openEventChat}
                                                    onOpenClientChat={openClientChat}
                                                    onChangeStage={changeStage}
                                                    onDelete={deleteEvent}
                                                    onManageDraftsman={manageDraftsman}
                                                    onToggleRevision={toggleRevision}
                                                    onAdditional={setAdditionalEvent}
                                                />
                                            </div>
                                        )}
                                    </Draggable>
                                )}
                            />
                        );
                    })}
                </Stack>
            </DragDropContext>

            <AdditionalStopDialog open={Boolean(additionalMove)} busy={additionalBusy} error={additionalError}
                target={additionalMove?.to} onCancel={() => setAdditionalMove(null)} onConfirm={confirmAdditionalMove} />
            {/* Мини-модалка доп. данных при перетаскивании */}
            {pendingMove && (
                <ModalKanbanTransition
                    open={!!pendingMove}
                    event={pendingMove.event}
                    fromStage={pendingMove.from}
                    toStage={pendingMove.to}
                    requiredFields={pendingMove.requiredFields}
                    employees={employeeAll}
                    onConfirm={handleTransitionConfirm}
                    onCancel={handleTransitionCancel}
                />
            )}

            {/* Смена стадии из меню карточки — тот же эндпоинт, что drag & drop */}
            {stageModal.open && (
                <ModalStagePicker
                    open={stageModal.open}
                    event={stageModal.event}
                    employees={employeeAll}
                    token={token}
                    onClose={() => setStageModal({ open: false, event: null })}
                    onChanged={(updatedEvent) => {
                        relocateEvent(updatedEvent);           // event пришёл с kanban_stage
                        setStageModal({ open: false, event: null });
                        loadSummary();
                        bumpBoardRefresh();
                    }}
                />
            )}

            {/* Управление чертёжником на стадии черчения (Задача 1) */}
            {draftsmanModal.open && (
                <ModalDraftsmanManage
                    open={draftsmanModal.open}
                    event={draftsmanModal.event}
                    employees={employeeAll}
                    token={token}
                    onClose={() => setDraftsmanModal({ open: false, event: null })}
                    onDone={() => { setDraftsmanModal({ open: false, event: null }); bumpBoardRefresh(); }}
                />
            )}

            {/* Импорт из Excel (реюз) */}
            <ModalImportApplications
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={() => loadColumn('applications', { reset: true })}
            />

            <Snackbar open={!!toast} onClose={() => setToast('')} autoHideDuration={3000}
                      color="danger" variant="soft"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                {toast}
            </Snackbar>
        </>
    );
}
